import React, { memo, useCallback } from 'react'

import { Icon, RotatingSpinner, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, object, oneOfType, number, shape, string } from 'prop-types'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { buildGeneratingRunUrl, parseGeneratingRunId } from 'Pods/ThreeDStudio/Hooks/generatingRunUrl'
import { setIsModelSettingsOpen, setOpenedModel } from 'Pods/ThreeDStudio/Store/actions/ui'

import { NOOP } from 'Utils/stableDefaults'

const iconSize = { height: '24px', width: '24px' }

function LoadingModelCard({
  content = '',
  onNavigate = NOOP,
  run = null,
  isActiveOnAiPage = false,
  isImagesTab = false,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  // The run id is the card's identity, and it is the one field that survives a page
  // reload: usePendingGeneratorRun restores in-flight runs with no `settings` at all,
  // so anything derived from settings is unavailable after a refresh. settings stays
  // optional enrichment - it carries the persisted slug and prompt for a run started
  // in this session, which only makes the URL prettier.
  const runId = run?.runId
  const settings = run?.settings
  const hasRunId = runId != null

  const generatingUrl = hasRunId ? buildGeneratingRunUrl(runId) : null
  // The `?run=` parameter carries the id verbatim, so this matches whether the run was
  // started in this session or restored after a reload - the case a slug-derived path
  // could never cover, since a restored run has no settings to build one from.
  const isActiveRunMatch = hasRunId
    && String(parseGeneratingRunId(location?.search ?? '')) === String(runId)
  // Legacy single-run behavior (free tiers): the card that replaced the "Generate New"
  // card is the active one while composing on /ai. With the new card present (paid
  // tiers), /ai highlights that card instead.
  const isAiPageMatch = isActiveOnAiPage && location?.pathname === '/ai'
  const isActive = hasRunId && (isActiveRunMatch || isAiPageMatch)

  const handleClick = useCallback(() => {
    // Always hand setOpenedModel something, even for a run restored after a reload that
    // knows only its id: coming from an opened model, the viewer keeps rendering that
    // model's file until openedModel is replaced, hiding the "Generating your 3D model"
    // banner this card is supposed to lead to.
    //
    // The empty object is the important part - openedModelToStatePatch short-circuits on
    // a payload with no id/providerModelId and clears the slice, so it never reaches the
    // per-provider parsers. Synthesizing a sparse `{ id, providerModelId }` instead does
    // reach them, and their `prompt: undefined` overwrites the store default and crashes
    // TextInstructions on `prompt.length`. Consumers that need to know which run is on
    // screen read it from the URL (see RelatedProducts), not from openedModel.
    setOpenedModel(settings ?? {})

    if (!hasRunId) {
      return
    }

    // Coming from an opened model, the Enhance 3D Model panel is showing that model's
    // actions - none of which apply to a run still generating. This one call swaps the
    // pair: it opens Generate 3D Model, closes the viewer sidebar and resets its tab.
    // It runs after setOpenedModel, which sets panel flags of its own.
    setIsModelSettingsOpen(true)

    if (`${location?.pathname ?? ''}${location?.search ?? ''}` !== generatingUrl) {
      navigate(generatingUrl)
    }
    onNavigate()
  }, [navigate, location, runId, hasRunId, settings, generatingUrl, onNavigate])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }, [handleClick])

  return (
    <div
      className={classnames('model-card', isActive && 'model-card--active')}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className={classnames('model-card__generating', isImagesTab && 'model-card__generating--images')}>
        <Icon svgComponent={RotatingSpinner} size={iconSize} color="primary" notSpaced />
        {content && <span className="model-card__generating-text">{content}</span>}
      </div>
    </div>
  )
}

LoadingModelCard.propTypes = {
  content: string,
  onNavigate: func,
  run: shape({
    runId: oneOfType([number, string]),
    providerModelId: string,
    settings: object,
  }),
  isActiveOnAiPage: bool,
  isImagesTab: bool,
}

export default memo(LoadingModelCard)
